import { useEffect } from "react";
import { createPortal } from "react-dom";
import { MapContainer, TileLayer, Marker, Popup, LayersControl } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import { Project, ProjectPhoto } from "types";
import Icon1 from "@assets/icons/Icon";

// Fix default marker icons broken by webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

interface Props {
  project: Project;
  currentPhotoId?: string;
  onClose: () => void;
  onSelectPhoto: (photoId: string) => void;
}

export default function PhotosMapModal({
  project,
  currentPhotoId,
  onClose,
  onSelectPhoto,
}: Props) {
  const photosWithCoords = (project.photos || []).filter(
    (p) => p.geo_location?.latitude && p.geo_location?.longitude,
  );

  // Center on current photo if it has coords, otherwise first photo with coords, otherwise France
  const currentPhoto = photosWithCoords.find((p) => p.id === currentPhotoId);
  const centerPhoto = currentPhoto ?? photosWithCoords[0];
  const defaultCenter: [number, number] = centerPhoto
    ? [centerPhoto.geo_location!.latitude, centerPhoto.geo_location!.longitude]
    : [46.8, 2.3];
  const defaultZoom = centerPhoto ? 14 : 6;

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ width: "90vw", height: "80vh", maxWidth: 1200 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-row items-center justify-between px-5 py-3 border-b border-neutral-200">
          <div className="flex flex-row items-center gap-2">
            <Icon1 name="location" size={20} />
            <span className="text-h2 font-semibold text-neutral-700">
              Localisation des photos
            </span>
            <span className="text-h3 text-neutral-400 ml-1">
              ({photosWithCoords.length} localisée
              {photosWithCoords.length > 1 ? "s" : ""})
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-700 transition-colors"
          >
            <Icon1 name="close" size={20} />
          </button>
        </div>

        {/* Map */}
        <div className="flex-1">
          {photosWithCoords.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-2 text-neutral-400">
              <Icon1 name="location" size={40} />
              <span className="text-h2">Aucune photo localisée</span>
              <span className="text-h3">
                Les photos prises depuis l'application mobile incluent
                automatiquement leur position GPS.
              </span>
            </div>
          ) : (
            <MapContainer
              center={defaultCenter}
              zoom={defaultZoom}
              style={{ width: "100%", height: "100%" }}
            >
              <LayersControl position="topright">
                <LayersControl.BaseLayer checked name="Satellite">
                  <TileLayer
                    attribution='&copy; <a href="https://www.esri.com/">Esri</a>, Maxar, Earthstar Geographics'
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    maxZoom={19}
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="Plan">
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                </LayersControl.BaseLayer>
              </LayersControl>
              <MarkerClusterGroup chunkedLoading>
                {photosWithCoords.map((photo) => {
                  const { latitude, longitude } = photo.geo_location!;
                  const isCurrent = photo.id === currentPhotoId;
                  return (
                    <Marker
                      key={photo.id}
                      position={[latitude, longitude]}
                      opacity={isCurrent ? 1 : 0.75}
                    >
                      <Popup>
                        <div className="flex flex-col gap-2" style={{ minWidth: 160 }}>
                          <img
                            src={photo.file.url}
                            alt=""
                            style={{
                              width: "100%",
                              height: 100,
                              objectFit: "cover",
                              borderRadius: 8,
                            }}
                          />
                          {isCurrent && (
                            <span className="text-xs text-blue-500 font-semibold">
                              Photo actuelle
                            </span>
                          )}
                          <button
                            onClick={() => {
                              onSelectPhoto(photo.id);
                              onClose();
                            }}
                            className="text-sm text-blue-600 hover:underline text-left"
                          >
                            Voir cette photo →
                          </button>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MarkerClusterGroup>
            </MapContainer>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
